// Where the user came from, for "back" controls that should return to the
// previous screen rather than a hard-coded destination.
//
// React Navigation can't answer this for us here: the drawer's own history is
// unreliable — MainMenu navigates with CommonActions.reset(), which wipes it,
// and screens that redirect on mount leave entries that bounce straight back.
// So we record it ourselves off the one event that always fires,
// NavigationContainer's onStateChange.
//
// Params are recorded alongside the name. A route like ClubProfile
// ('clubs/profile/:clubId') is meaningless without them — navigating back by
// name alone lands on a screen whose `route.params.clubId` is undefined.

import create from 'zustand';

export interface VisitedRoute {
  name: string;
  params?: Record<string, any>;
}

// Enough depth to skip past a screen or two that redirect on mount, without
// holding onto history nobody will ever walk back through.
const MAX_ENTRIES = 10;

interface NavHistoryState {
  /** Most recent last. */
  stack: VisitedRoute[];
  /** Called on every navigation state change with the resolved route. */
  setRoute: (name: string, params?: Record<string, any>) => void;
  /**
   * Most recent entry that isn't the current screen and isn't excluded.
   * `exclude` exists for screens that redirect back to the caller — walking
   * into one of those is an immediate bounce.
   */
  getPrevious: (exclude?: string[]) => VisitedRoute | null;
}

export const useNavHistoryStore = create<NavHistoryState>((set, getState) => ({
  stack: [],

  setRoute: (name, params) => {
    if (!name) {
      return;
    }
    const {stack} = getState();
    const last = stack[stack.length - 1];
    // Params-only changes (e.g. switching tabs within School of Chess) re-fire
    // onStateChange with the same route. Treating those as navigation would
    // make "back" point at the screen you're already on — just refresh the
    // params on the existing entry instead.
    if (last?.name === name) {
      const next = stack.slice(0, -1);
      next.push({name, params});
      set({stack: next});
      return;
    }
    set({stack: [...stack, {name, params}].slice(-MAX_ENTRIES)});
  },

  getPrevious: (exclude = []) => {
    const {stack} = getState();
    const current = stack[stack.length - 1]?.name;
    for (let i = stack.length - 2; i >= 0; i--) {
      const entry = stack[i];
      if (entry.name !== current && !exclude.includes(entry.name)) {
        return entry;
      }
    }
    return null;
  },
}));

export default useNavHistoryStore;
